import { t } from '@lingui/core/macro';
import { useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Form/pickers';

import type { AttributeOperatorType, RuleEntityAttributeType } from './types';

type Props = {
  attribute: RuleEntityAttributeType | undefined;
  value?: AttributeOperatorType;
  disabled?: boolean;
  onChange?: (value: AttributeOperatorType | null) => void;
  onBlur?: () => void;
};

const getOperatorOptions = (attribute?: RuleEntityAttributeType) => {
  if (!attribute) {
    return [];
  }

  switch (attribute.type) {
    case 'string':
      return attribute.display === 'picklist'
        ? [
            { label: t`is one of`, value: 'eq' },
            { label: t`is not one of`, value: 'ne' },
          ]
        : [
            { label: t`is one of`, value: 'eq' },
            { label: t`is not one of`, value: 'ne' },
            { label: t`contains`, value: 'contains' },
            { label: t`does not contain`, value: 'notContains' },
          ];
    case 'number':
      return [
        { label: t`is equal to`, value: 'eq' },
        { label: t`is not equal to`, value: 'ne' },
        { label: t`is greater than`, value: 'gt' },
        { label: t`of at least`, value: 'gte' },
        { label: t`is less than`, value: 'lt' },
        { label: t`is less than or equal to`, value: 'lte' },
      ];
    case 'boolean':
      throw new Error('Boolean attributes are not supported yet');
    case 'temporal':
      throw new Error('Temporal attributes are not supported yet');
    default:
      throw new Error(`Unsupported attribute type: ${attribute.type}`);
  }
};

const RuleBuilderOperatorSelect = ({
  attribute,
  value,
  disabled = false,
  onBlur,
  ...props
}: Props) => {
  const operatorOptions = useMemo(() => getOperatorOptions(attribute), [attribute]);

  return (
    <Select disabled={disabled} items={operatorOptions} value={value} {...props}>
      <SelectTrigger onBlur={onBlur}>
        <SelectValue placeholder={t`Select an operator...`} />
      </SelectTrigger>
      <SelectContent>
        {operatorOptions.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default RuleBuilderOperatorSelect;
